import React, { useState, useEffect } from "react";
import MasterTable from "../common/MasterTable";
import Loader from "../Loader/Loader";
import { axiosPostApi } from "../../helpers/interceptorServices";
import { apiList } from "../../helpers/apiList";
import CustomAlert from "../../helpers/CustomAlert";
import AddPickupAddressModal from "./AddPickupAddressModal";
import HeaderWithBreadcrumb from "../common/HeaderWithBreadcrumb";
import PickupAddressFilterModal from "./PickupAddressFilterModal";
import FilterBar from "../common/FilterBar";

// Fields the search box looks in (the list is filtered in the browser)
const SEARCH_FIELDS = ["addressTitle", "addressee", "phone", "email", "fullAddress", "city", "state", "pinCode"];

const defaultFilterData = {
  addressTitle: "",
  phone: "",
  search: "",
};

const PickupAddress = () => {
  // const pageSize = 10;
  const [pageSize, setPageSize] = useState(10);
  const loggedInUserName = sessionStorage.getItem("username");
  const [loading, setLoading] = useState(true);
  const [noData, setNoData] = useState(false);
  const [pickupaddressData, setPickupaddressData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageDetails, setPageDetails] = useState(null);
  const [editData, setEditData] = useState({});
  const [modalAction, setModalAction] = useState("");
  const [columnData, setColumnData] = useState("");
  const [isModalOpenCreate, setIsModalOpenCreate] = useState(false);
  const [isModalOpenFilter, setIsModalOpenFilter] = useState(false);
  const [filterData, setFilterData] = useState(defaultFilterData);
  const [rawMasterData, setRawMasterData] = useState([]);

  const openModalCreate = () => {
    setIsModalOpenCreate(true);
  };

  const closeModalCreate = () => {
    setIsModalOpenCreate(false);
  };

  const openModalFilter = () => {
    setIsModalOpenFilter(true);
  };

  const closeModalFilter = () => {
    setIsModalOpenFilter(false);
  };

  const pickupaddressHeader = [
    "Address Title",
    "Addressee",
    "Full Address",
    "Phone",
    "PIN Code",
    "City",
    "State",
    "Email",
  ];

  const pickupaddressHeaderToKeyMap = {
    "Address Title": "addressTitle",
    Addressee: "addressee",
    "Full Address": "fullAddress",
    Phone: "phone",
    "PIN Code": "pinCode",
    City: "city",
    State: "state",
    Email: "email",
  };

  const modifyStructure = (response) => {
    return response?.map((item, index) => ({
      ...item,
      id: index + 1,
    }));
  };

  const fetchPickupAddressData = (
    page = currentPage,
    size = pageSize,
    currentFilters = filterData
  ) => {
    const payload = {
      username: loggedInUserName,
    };

    setLoading(true);

    const hasFilters = Boolean(
      (currentFilters?.addressTitle &&
        currentFilters.addressTitle.trim() !== "") ||
        (currentFilters?.phone && currentFilters.phone.trim() !== "") ||
        (currentFilters?.search && currentFilters.search.trim() !== "")
    );

    const queryPageSize = hasFilters ? 1000 : size;
    const queryPageNo = hasFilters ? 1 : page;

    axiosPostApi(
      `${apiList.FETCH_PICKUPADDRESS}?&pageSize=${queryPageSize}&pageNo=${queryPageNo}`,
      payload
    )
      .then((response) => {
        if (
          response?.data?.status === true &&
          response?.data?.respCode === "00"
        ) {
          let modifiedData = modifyStructure(response?.data?.data) || [];
          setRawMasterData(modifiedData);

          if (hasFilters) {
            if (
              currentFilters?.addressTitle &&
              currentFilters.addressTitle.trim() !== ""
            ) {
              const targetTitle = currentFilters.addressTitle
                .trim()
                .toLowerCase();
              modifiedData = modifiedData.filter(
                (item) =>
                  (item?.addressTitle || "").trim().toLowerCase() ===
                  targetTitle
              );
            }

            if (currentFilters?.phone && currentFilters.phone.trim() !== "") {
              const targetPhone = currentFilters.phone.trim().toLowerCase();
              modifiedData = modifiedData.filter(
                (item) =>
                  (item?.phone || "").trim().toLowerCase() === targetPhone
              );
            }

            if (currentFilters?.search && currentFilters.search.trim() !== "") {
              const term = currentFilters.search.trim().toLowerCase();
              modifiedData = modifiedData.filter((item) =>
                SEARCH_FIELDS.some((f) =>
                  String(item?.[f] ?? "").toLowerCase().includes(term),
                ),
              );
            }

            const totalCount = modifiedData.length;
            const totalPages = Math.ceil(totalCount / size) || 1;
            const startIndex = (page - 1) * size;
            const paginatedData = modifiedData
              .slice(startIndex, startIndex + size)
              .map((item, idx) => ({
                ...item,
                id: startIndex + idx + 1,
              }));

            // same shape as the server's pageDetails (the table reads pageNo / noOfPages)
            setPageDetails({
              pageNo: page,
              currentPage: page,
              pageSize: size,
              totalRecords: totalCount,
              noOfPages: totalPages,
              totalPages: totalPages,
            });
            setPickupaddressData(paginatedData);
            setNoData(paginatedData.length === 0);
          } else {
            setPageDetails(response?.data?.pageDetails);
            setPickupaddressData(modifiedData);
            setNoData(modifiedData?.length === 0);
          }
        } else {
          setNoData(true);
          setPickupaddressData([]);
        }
      })
      .catch((error) => {
        CustomAlert({
          title: "Error",
          text: "An error occurred while fetching the data. Please try again later.",
          icon: "error",
          confirmButtonText: "Retry",
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchPickupAddressData(1, pageSize, defaultFilterData);
  }, []);

  const handlePickupAddress = (data, actionType = "view") => {
    if (!["add", "modify", "view"].includes(actionType)) {
      return;
    }
    setModalAction(actionType);
    setColumnData(data);
    if (actionType === "add") {
      setEditData({});
      openModalCreate(true);
    } else {
      let pickupData = pickupaddressData.find((item) => item?.id === data?.id);
      if (pickupData) {
        setEditData(pickupData);
        openModalCreate(true);
      }
    }
  };

  const applyFilters = (newFilters = filterData) => {
    setFilterData(newFilters);
    setCurrentPage(1);
    fetchPickupAddressData(1, pageSize, newFilters);
  };

  const handleReset = () => {
    setFilterData(defaultFilterData);
    setCurrentPage(1);
    fetchPickupAddressData(1, pageSize, defaultFilterData);
  };

  const pageSizeCount = pageDetails === null ? 0 : pageDetails.pageSize;
  const totalRecord = pageDetails === null ? 0 : pageDetails.totalRecords;

  return (
    <>
      <HeaderWithBreadcrumb title="Pickup Address" breadcrumb="Master" />
      <FilterBar
        values={filterData}
        onChange={applyFilters}
        search={{ key: "search", placeholder: "Search title, name, phone, address, city or pincode" }}
      />
      <div>
        <div className="bg-[#FFFFFF] shadow-custom rounded-[14px] px-[10px]">
          {loading ? (
            <Loader />
          ) : (
            <MasterTable
              loading={loading}
              headers={pickupaddressHeader}
              data={pickupaddressData}
              filterFields={["addressTitle", "phone", "addressee", "city", "state"]}
              entityType="Pickup Address"
              headerToKeyMap={pickupaddressHeaderToKeyMap}
              handleAction={handlePickupAddress}
              noData={noData}
              pageDetails={pageDetails}
              currentPage={currentPage}
              pageSize={pageSize}
              // pageSize={pageSizeCount}
              totalRecord={totalRecord}
              fetchNextData={(page, size) => {
                setCurrentPage(page);
                setPageSize(size);
                fetchPickupAddressData(page, size, filterData);
              }}
              setCurrentPage={setCurrentPage}
              setPageSize={setPageSize}
              openModalFilter={openModalFilter}
              handleReset={handleReset}
              filterData={filterData}
              fetchData={() => {}}
              hideFilterControls
            />
          )}
          {isModalOpenCreate ? (
            <AddPickupAddressModal
              show={isModalOpenCreate}
              handleClose={closeModalCreate}
              editData={editData}
              action={modalAction}
              callApi={() => fetchPickupAddressData(1, pageSize, defaultFilterData)}
              columnData={columnData}
            />
          ) : null}

          {isModalOpenFilter && (
            <PickupAddressFilterModal
              show={isModalOpenFilter}
              handleClose={closeModalFilter}
              filterData={filterData}
              setFilterData={setFilterData}
              handleReset={handleReset}
              pickupList={rawMasterData}
              applyFilters={applyFilters}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default PickupAddress;
